import {
  type BlazeTableSchema,
  type DatasetSchema,
  type ElementSchema,
  isBlazeTableSchema,
  isDatasetSchema,
  isElementSchema,
  isWorkflowSchema,
  type PageContextSchema,
  type WorkflowSchema,
} from '@blaze-shared/schema-domain';

/** Schema types from which we're able to create the PageContextSchema */
export type PageContextSourceSchema =
  | BlazeTableSchema
  | DatasetSchema
  | ElementSchema
  | WorkflowSchema;

/**
 * Builds the Page Context (`$$Page`) schema from the schema of its data source.
 *
 * `pageContextId` identifies a concrete page context instance (a loaded data row).
 * When there is none - e.g. when deriving the schema purely for intellisense on a Detail Page
 * without a selected row - it defaults to the `pageId`, giving a stable synthetic id.
 */
export function createPageContextSchema(
  schema: PageContextSourceSchema,
  pageId: string,
  pageContextId: string = pageId,
): PageContextSchema {
  let datasetId: string | undefined;
  let elementId: string | undefined;
  let workflowId: string | undefined;

  if (isDatasetSchema(schema) || isBlazeTableSchema(schema)) {
    datasetId = schema.id;
  } else if (isWorkflowSchema(schema)) {
    datasetId = schema.tableId;
    workflowId = schema.workflowId;
  } else if (isElementSchema(schema)) {
    datasetId = schema.datasetId;
    elementId = schema.elementId;
    workflowId = schema.workflowId;
  } else {
    throw new Error('Incompatible schema type');
  }

  return {
    columns: schema.columns,
    datasetId,
    elementId,
    id: pageContextId,
    name: schema.name,
    pageId,
    workflowId,
  };
}
